import { Filter } from 'lucide-react';
import React from 'react';

/**
 * SectionMiniHeader — Terminal-style mini header above the table/content area.
 * Shows: [Icon] SECTION TITLE  [count badge]  [filter toggle button]
 *
 * Props:
 *  - icon: Lucide icon component
 *  - title: Section name (e.g. "Items Catalog", "Audit Trail")
 *  - count: Number of items shown
 *  - filtersVisible: boolean — whether the filter sidebar is currently visible
 *  - onToggleFilters: () => void — callback to show/hide sidebar
 *  - children: optional right-side slot for extra actions
 */
const SectionMiniHeader = ({
    icon: Icon,
    title,
    subtitle,
    count,
    filtersVisible = true,
    onToggleFilters,
    children,
    isTerminal = false,
}) => {
    if (!title && !children && !onToggleFilters) return null;

    return (
        <div className={`flex items-center justify-between h-10 ${isTerminal ? 'mt-2 mb-1' : 'mb-[-4px]'} shrink-0 sticky top-0 z-30 group px-0`}>
            {/* Left: Icon + Title */}
            <div className="flex items-center gap-2">
                {Icon && (
                    <Icon size={12} className="text-accent group-hover:scale-125 transition-transform" strokeWidth={3} />
                )}
                {title && (
                    <div className="flex flex-col justify-center">
                        <h2 className="font-black text-slate-400 group-hover:text-primary transition-colors text-[9px] font-black uppercase tracking-[0.2em] leading-tight">
                            {title}
                        </h2>
                        {subtitle && (
                            <span className="text-[7px] font-bold text-slate-300 uppercase tracking-widest mt-0.5">
                                {subtitle}
                            </span>
                        )}
                    </div>
                )}
                {count !== undefined && count !== null && (
                    <span className="ml-1 text-[8px] font-black text-slate-400 uppercase tracking-widest px-2 py-1 bg-slate-50 rounded-full border border-slate-100">
                        {count}
                    </span>
                )}
            </div>

            {/* Right: Optional extra actions + Filter toggle */}
            <div className="flex items-center gap-2">
                {children}
                {onToggleFilters && (
                    <button
                        tabIndex={-1}
                        onClick={onToggleFilters}
                        title={filtersVisible ? 'Hide Filters' : 'Show Filters'}
                        className={`flex items-center gap-2 p-3 rounded-2xl border transition-all active:scale-95 cursor-pointer ${filtersVisible
                            ? 'bg-primary/5 border-primary/10 text-primary'
                            : ' border-slate-200 text-slate-400 hover:text-primary hover:border-primary/20'
                            }`}
                    >
                        <Filter size={16} strokeWidth={2.5} />
                        <span className="hidden sm:inline text-[9px] font-black uppercase tracking-widest">
                            {filtersVisible ? 'Hide' : 'Filters'}
                        </span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default React.memo(SectionMiniHeader);
